/**
 * 管理员认证表单（登录 / 动态口令绑定）的**提交前校验**与**失败文案**。
 *
 * ## 为什么把这两件事从页面里抽出来
 *
 * 2026-10-18 用户实测：管理员登录页**密码没输入就点「登录」**，屏幕上冒出一串
 * 形如 `[{"code":"too_small",…,"message":"请输入密码","path":["password"]}]` 的 JSON。
 * 两个问题叠在一起：
 *
 *   1. 页面**没有任何前端校验**（表单是 `noValidate`，`required` 属性不生效），
 *      空密码照样发请求 —— 请求打到后端，`loginSchema.parse` 抛 ZodError，
 *      经过 Fastify 默认错误处理器变成 500 + 一串 JSON（链路见 `./api/failure.ts`）；
 *   2. 页面 catch 里 `setError(toErrorMessage(err))` 把 `message` 原样显示。
 *
 * 修法分两层，**都在这里可单测**：
 *   · `planAdminLogin()` —— 用户名 / 密码为空时直接返回人话，**不构造请求**；
 *   · `runAdminLogin()` —— 只在计划合法时才调用 `login`，失败一律经
 *     `describeFailureMessage()`（统一出口）变成一句人话，兜底「登录失败，请重试」。
 *
 * ⚠️ 这里**只做非空校验**，不校验长度 / 字符集，也不碰密码是否正确：
 *    "用户名或密码不正确" 必须仍然由后端判定（那里才有锁定、验证码、动态口令）。
 */
import type { ErrorCode } from '@fht/shared';
import type { LoginPayload } from './api/auth';
import type { MeResponse } from './api/types';
import { describeFailureMessage } from './api/failure';

/** 登录失败时的兜底文案（拿不到人话才用）。 */
export const ADMIN_LOGIN_FAILED = '登录失败，请重试';
/** 动态口令绑定失败时的兜底文案。 */
export const ADMIN_TOTP_FAILED = '绑定失败，请重试';

/** 管理员登录表单的当前值（页面 state 原样传进来）。 */
export interface AdminLoginForm {
  username: string;
  password: string;
  totpCode: string;
  /** 后端在失败次数达到阈值后强制要图形验证码（前端据此显示输入框）。 */
  captchaActive: boolean;
  captchaId: string | null;
  captchaAnswer: string;
  /** 本机记录的连续失败次数（回传给后端，见 `api/auth.ts` 的失败计数）。 */
  failures: number;
}

/** 提交计划：要么"直接提示、不发请求"，要么"带着这份 payload 去登录"。 */
export type AdminLoginPlan =
  | { kind: 'invalid'; message: string }
  | { kind: 'submit'; payload: LoginPayload };

/**
 * 决定这次点击要不要发请求（**纯函数**，故可直接断言"没有构造请求"）。
 *
 * 文案固定为「请填写用户名」「请填写密码」「请输入图形验证码」：
 *  · 不用浏览器 `required` 的原生气泡：文案不可控、也没法被测试锁定；
 *  · 顺序是用户名 → 密码 → 验证码，与表单从上到下的视觉顺序一致。
 */
export function planAdminLogin(form: AdminLoginForm): AdminLoginPlan {
  const username = form.username.trim();
  if (username === '') return { kind: 'invalid', message: '请填写用户名' };
  if (form.password === '') return { kind: 'invalid', message: '请填写密码' };
  if (form.captchaActive && (!form.captchaId || form.captchaAnswer.trim() === '')) {
    return { kind: 'invalid', message: '请输入图形验证码' };
  }

  return {
    kind: 'submit',
    payload: {
      role: 'system_admin',
      username,
      password: form.password,
      // 管理会话更短、也不参与"30 天免密"（后端还会强制一次，这里只是不误导用户）
      rememberDevice: false,
      captchaFailures: form.failures,
      ...(form.captchaActive && form.captchaId ? { captchaId: form.captchaId } : {}),
      ...(form.captchaActive && form.captchaAnswer ? { captchaAnswer: form.captchaAnswer } : {}),
      ...(form.totpCode ? { totpCode: form.totpCode } : {}),
    },
  };
}

/** 从任意异常上取后端错误码（页面据此决定"要不要显示动态口令 / 记一次失败"）。 */
export function errorCodeOf(error: unknown): ErrorCode | null {
  if (typeof error !== 'object' || error === null) return null;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' && code !== '' ? (code as ErrorCode) : null;
}

/** 一次登录尝试的结果（页面据此做副作用：显示错误、换验证码、跳转）。 */
export type AdminLoginResult =
  | { kind: 'invalid'; message: string }
  | { kind: 'failed'; message: string; code: ErrorCode | null }
  | { kind: 'ok'; me: MeResponse };

/**
 * 走完一次管理员登录：**校验 → 请求 → 失败归一**。
 *
 * `onStart` 只在真的准备发请求时调用（页面用它在按钮上转圈，避免
 * "空密码也闪一下 loading"）。
 */
export async function runAdminLogin(
  form: AdminLoginForm,
  login: (payload: LoginPayload) => Promise<MeResponse>,
  onStart?: () => void,
): Promise<AdminLoginResult> {
  const plan = planAdminLogin(form);
  if (plan.kind === 'invalid') return plan;

  onStart?.();
  try {
    return { kind: 'ok', me: await login(plan.payload) };
  } catch (err) {
    return {
      kind: 'failed',
      message: describeFailureMessage(err, ADMIN_LOGIN_FAILED),
      code: errorCodeOf(err),
    };
  }
}

/**
 * 动态口令输入的问题（`null` = 没问题）。
 *
 * 6 位数字是后端 `totpVerifySchema` 的硬要求（`/^\d{6}$/`）；前端先说一遍，
 * 省一次注定 400 的往返，也避免把后端的校验文案端给用户。
 */
export function totpCodeIssue(code: string): string | null {
  return /^\d{6}$/.test(code.trim()) ? null : '请输入 6 位动态口令';
}
